import { useMemo, useState } from 'react';

import { useDebouncedValue } from '@mantine/hooks';
import { useMutation, useQuery } from 'react-query';

import { useQueryClient } from '../../../contexts/queryClient';

function useDataFetch({
    id,
    fetchFn,
    fetchProps,
    onMutate,
    onMutationSuccess,
    onMutationError,
}) {
    const [params, setParams] = useState({
        page: 1,
        limit: 40,
        search: '',
    });

    const [debouncedParams] = useDebouncedValue(params, 200);

    const { queryClient } = useQueryClient();

    const queryKey = useMemo(() => (
        [id, {
            ...fetchProps,
            ...debouncedParams,
        }]
    ), [id, debouncedParams, fetchProps]);

    const {
        isError, data, error, isPreviousData, isFetching,
    } = useQuery(queryKey, fetchFn, {
        retry: 1,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    function refreshData() {
        queryClient.invalidateQueries(queryKey);
    }

    const mutatation = useMutation(({ mutationFn, mutationProps }) => mutationFn({
        ...mutationProps,
        ...fetchProps,
    }), {
        onMutate,
        onSuccess: onMutationSuccess || refreshData,
        onError: onMutationError || refreshData,
    });

    function handleParamsSet(newData) {
        setParams((prevData) => ({
            ...prevData,
            ...newData,
        }));
    }

    return {
        data,
        queryKey,
        refreshData,
        isError,
        error,
        isPreviousData,
        isFetching,
        mutatation,
        params,
        setParams: handleParamsSet,
    };
}

export default useDataFetch;
